import { supabase } from '@/lib/supabase';
import type { ToolAppGrantPrincipalKind, ToolAppKind, ToolAppStatus, ToolAppVisibility } from '@shared/types/tool-apps';

/**
 * F5-0: an app (read surfaces or Forge tool) and who can open it. Access is set
 * in one transaction by `set_tool_app_access_f5` (owner or tenant manager).
 */
export interface AppSummary {
  id: string;
  key: string;
  title: string;
  kind: ToolAppKind;
  visibility: ToolAppVisibility;
  status: ToolAppStatus;
  ownerUserId: string;
  /** The flow of an app of kind « flow ». */
  flowId: string | null;
}

export interface AccessPrincipal {
  kind: ToolAppGrantPrincipalKind;
  id: string;
  label: string;
  detail: string;
}

export const principalKey = (principal: { kind: ToolAppGrantPrincipalKind; id: string }) => `${principal.kind}:${principal.id}`;

type AppRow = { id: string; key: string; title: string; kind: ToolAppKind; visibility: ToolAppVisibility; status: ToolAppStatus; owner_user_id: string; flows: Array<{ id: string }> | null };

export const loadAppByKey = async (key: string): Promise<AppSummary | null> => {
  const { data, error } = await supabase.from('tool_apps')
    .select('id, key, title, kind, visibility, status, owner_user_id, flows(id)').eq('key', key).maybeSingle();
  if (error) throw new Error('Impossible de charger cette application.');
  const row = data as AppRow | null;
  return row ? {
    id: row.id, key: row.key, title: row.title, kind: row.kind, visibility: row.visibility, status: row.status,
    ownerUserId: row.owner_user_id, flowId: row.flows?.[0]?.id ?? null,
  } : null;
};

/** The app key of a Forge tool, or null when the flow has none (sandbox, standard tool). */
export const loadAppKeyOfFlow = async (flowId: string): Promise<string | null> => {
  const { data, error } = await supabase.from('flows').select('app:tool_apps(key)').eq('id', flowId).maybeSingle();
  if (error) return null;
  const app = (data as { app: { key: string } | null } | null)?.app;
  return app?.key ?? null;
};

export const loadAppAccess = async (appId: string): Promise<{ canManage: boolean; selected: string[] }> => {
  const [{ data: canManage }, { data: grants, error }] = await Promise.all([
    supabase.rpc('can_manage_tool_app', { p_tool_app_id: appId }),
    supabase.from('tool_app_grants').select('principal_kind, principal_id').eq('tool_app_id', appId),
  ]);
  if (error) throw new Error('Impossible de charger les accès de cette application.');
  return {
    canManage: canManage === true,
    selected: (grants ?? []).map((grant) => principalKey({ kind: grant.principal_kind, id: grant.principal_id })),
  };
};

/** Teams first, then the other people of the tenant. */
export const loadAccessRoster = async (currentUserId: string): Promise<AccessPrincipal[]> => {
  const [members, teams] = await Promise.all([
    supabase.from('tenant_members').select('user_id, display_name, tenant_role').order('display_name'),
    supabase.from('teams').select('id, name').is('archived_at', null).order('name'),
  ]);
  if (members.error || teams.error) throw new Error('Impossible de charger les personnes et équipes disponibles.');
  return [
    ...(teams.data ?? []).map((team) => ({ kind: 'team' as const, id: team.id as string, label: team.name as string, detail: 'Équipe' })),
    ...(members.data ?? []).filter((member) => member.user_id !== currentUserId).map((member) => ({
      kind: 'user' as const,
      id: member.user_id as string,
      label: (member.display_name as string | null)?.trim() || 'Membre sans nom affiché',
      detail: member.tenant_role === 'manager' ? 'Manager' : 'Membre',
    })),
  ];
};

const ACCESS_ERRORS: Record<string, string> = {
  tool_app_access_forbidden: 'Seuls le créateur de l’application et les managers règlent son accès.',
  tool_app_grants_required: 'Choisissez au moins une équipe ou une personne.',
  tool_app_grant_principal_invalid: 'Une des équipes ou personnes choisies n’existe plus.',
};

export const saveAppAccess = async (appId: string, visibility: ToolAppVisibility, selected: string[]): Promise<void> => {
  const grants = visibility === 'restricted'
    ? selected.map((key) => {
      const [kind, id] = key.split(':');
      return { principalKind: kind, principalId: id };
    })
    : [];
  const { error } = await supabase.rpc('set_tool_app_access_f5', { p_tool_app_id: appId, p_visibility: visibility, p_grants: grants });
  if (error) throw new Error(ACCESS_ERRORS[error.message] ?? 'L’accès n’a pas pu être enregistré. Réessayez.');
};
